<script>
  import { format } from 'svelte-i18n';

  import { Preview } from '@client/components/preview';

  import { ACTIVITY_EVENTS } from '@client/enums';

  /**
   * @typedef {Object} ActivityNote
   * @property {string} hash - Unique identifier used to build preview URLs.
   * @property {number} [slide] - Optional slide number for previewing specific pages.
   */

  /**
   * @typedef {Object} ActivityEntry
   * @property {string} [verb] - The event type (e.g., 'clicked', 'slideviewed').
   * @property {string} [objecttype] - The type of object involved in the event.
   * @property {ActivityNote} note - Additional metadata related to the event.
   */

  /**
   * A single activity history entry.
   * Used to determine how to render message and slide previews.
   * @type {ActivityEntry}
   */
  export let entry;

  /**
   * Whether to show the thumbnail image preview.
   * This is toggled on mouseenter/mouseleave.
   * @type {boolean}
   */
  let showThumbnailPreview = false;

  /**
   * Whether the activity event is not related to a slide view.
   * Used to determine how to render the message.
   * @type {boolean}
   */
  $: nonSlideEvent = entry.verb === 'clicked' || entry.verb === 'visited' || entry.objecttype === 'videoactivity';
</script>

<span class="message">
  {#if entry.replied}
    {$format('label.REPLIED')}
  {/if}

  {#if entry.discarded}
    {$format('label.DISCARDED_EMAIL')}
  {:else if nonSlideEvent}
    {$format(ACTIVITY_EVENTS[entry.objecttype]?.message)}
  {:else}
    {$format(ACTIVITY_EVENTS[entry.verb]?.message)}

    {#if ACTIVITY_EVENTS.slideviewed.id === entry.verb}
      {@const slideThumbnailUrl = `https://www.sendergen.com/v2/view/${entry.note.hash}/Slide ${entry.note?.slide}/thumbnail.png`}
      <a
        class="image-preview"
        href={slideThumbnailUrl}
        title={slideThumbnailUrl}
        on:mouseenter={() => (showThumbnailPreview = true)}
        on:mouseleave={() => (showThumbnailPreview = false)}
        target="_blank"
      >
        {$format('label.PAGE')}
        {entry.note?.slide}
        <Preview
          minWidth="auto"
          disableZoom
          show={showThumbnailPreview}
          content="<img src='{slideThumbnailUrl}' loading='lazy' />"
          alt="Slide {entry.note?.slide}"
        />
      </a>
    {/if}
  {/if}
</span>

<style lang="scss" src="./item-message.scss"></style>
